import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { Box } from '~/components/elements/Box'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledSplitter,
  type StyledSplitterProps,
} from '~/components/elements/StyledSplitter'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeTreeViewBox } from '~/components/features/CustomerAttributeTreeViewBox'
import { DataDisplayTypeAtomSwitcher } from '~/components/features/DataDisplayTypeAtomSwitcher'
import { GroupingPanel } from '~/components/features/GroupingPanel'
import { QuestionTreeViewBox } from '~/components/features/QuestionTreeViewBox'
import { ResultViewerSwitcher } from '~/components/features/ResultViewerSwitcher'
import {
  SharedBrandGroupSelector,
  type SharedBrandGroupSelectorProps,
} from '~/components/features/SharedBrandGroupSelector'
import { useRestoreOnMount } from '~/hooks/useRestoreOnMount'
import { useScrollEffect } from '~/hooks/useScrollEffect'
import { exists } from '~/utils'

import {
  brand1IdAtom,
  brand2IdAtom,
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  groupAtom,
  groupInfosAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  questionIdsAtom,
  resultViewerTypeAtom,
  tabulationQueryDataAtom,
} from '../atoms'
import { useRestoreParams } from '../hooks/restoringParams/useRestoreParams'
import { useSurveyId } from '../hooks/useSurveyId'
import { useTabulationQuery } from '../hooks/useTabulationQuery'
import { pathKey } from '../utils'

import { Brand1SelectorContainer } from './Brand1SelectorContainer'
import { Brand2SelectorContainer } from './Brand2SelectorContainer'
import { ClearGroupsButton } from './ClearGroupsButton'
import { GroupCountsFiguresContainer } from './GroupCountsFiguresContainer'
import styles from './OverlapAnalysis.css'
import { OverlapTable } from './OverlapTable'
import { TabulateButton } from './TabulateButton'

export const OverlapAnalysis = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('advancedAnalysis'),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const isExecuting = useTabulationQuery()

  const [panes, setPanes] = React.useState<StyledSplitterProps['panes']>([
    { size: 'calc(0.5rem + 96px + 72px * 6 + 0.25rem + 30px)' },
    {},
  ])

  const setBrand1Id = useSetAtom(brand1IdAtom)
  const setBrand2Id = useSetAtom(brand2IdAtom)
  const setGroupInfos = useSetAtom(groupInfosAtom)
  const setIsPendingTabulation = useSetAtom(isPendingTabulationAtom)

  const handleBrandGroupChange = React.useCallback<
    EH<SharedBrandGroupSelectorProps, 'onChange'>
  >(() => {
    setBrand1Id(null)
    setBrand2Id(null)
    setGroupInfos([])
    setIsPendingTabulation(false)
  }, [setBrand1Id, setBrand2Id, setGroupInfos, setIsPendingTabulation])

  const suspendTabulation = React.useCallback(() => {
    setIsPendingTabulation(true)
  }, [setIsPendingTabulation])

  const restoreParams = useRestoreParams()
  useRestoreOnMount(restoreParams, isTabulationReadyAtom)

  const tabulationQueryData = useAtomValue(tabulationQueryDataAtom)
  useScrollEffect(exists(tabulationQueryData))

  return (
    <PageContainer breadcrumbs={breadcrumbs} executing={isExecuting}>
      <StyledSplitter
        className={styles.root}
        panes={panes}
        splitBarSpacing={2}
        onChange={setPanes}
      >
        <VStack spacing={4}>
          <LabelingContainer label={t('brand_group')}>
            <VStack spacing={1}>
              <SharedBrandGroupSelector onChange={handleBrandGroupChange} />

              <Box className={styles.brands}>
                <Typography variant="heading2">{t('brand_1')}</Typography>

                <Brand1SelectorContainer />

                <Typography variant="heading2">{t('brand_2')}</Typography>

                <Brand2SelectorContainer />
              </Box>
            </VStack>
          </LabelingContainer>

          <LabelingContainer label={t('switch_view')}>
            <DataDisplayTypeAtomSwitcher
              dataDisplayTypeAtom={dataDisplayTypeAtom}
            />
          </LabelingContainer>

          <LabelingContainer label={t('select_group')}>
            <GroupingPanel groupAtom={groupAtom} />
          </LabelingContainer>

          <LabelingContainer label={t('overlap_selection')}>
            <VStack
              align="end"
              className={styles.overlapTableContainer}
              inline={true}
              spacing={1}
            >
              <ClearGroupsButton />

              <OverlapTable />
            </VStack>
          </LabelingContainer>
        </VStack>

        <VStack spacing={8}>
          <VStack spacing={4}>
            <LabelingContainer label={t('switch_view')}>
              <ResultViewerSwitcher
                resultViewerTypeAtom={resultViewerTypeAtom}
              />
            </LabelingContainer>

            <LabelingContainer label={t('select_questionnaire')}>
              <QuestionTreeViewBox
                isMatrixQuestionSelectable={true}
                questionIdsAtom={questionIdsAtom}
                onSelectedQuestionIdChange={suspendTabulation}
              />
            </LabelingContainer>

            <LabelingContainer label={t('customized_segments_selection')}>
              <CustomerAttributeTreeViewBox
                customerAttributeIdsAtom={customerAttributeIdsAtom}
                onSelectedCustomerAttributeIdsChange={suspendTabulation}
              />
            </LabelingContainer>

            <TabulateButton className={styles.tabulateButton} />
          </VStack>

          <GroupCountsFiguresContainer />
        </VStack>
      </StyledSplitter>
    </PageContainer>
  )
}
